import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { Type, UndoRedoAction } from '../hooks/useUndoRedo';
import { IImage, ILabel, ImageDimensions, ImagePosition, SavedLabel } from '../types';
import { UndoRedoContext } from './undoRedoContext';

export enum ActionTypes {
  SET_IMAGES = 'SET_IMAGES',
  SET_LABELS = 'SET_LABELS',
  REPLACE_IMAGE = 'REPLACE_IMAGE',
}

const initialState: IState = {
  labels: [],
  images: [],
};

export interface IState {
  images: Array<IImage>;
  labels: Array<ILabel>;
}

export interface IWordsImage {
  imageUUIDtoReplace: string;
  uuid: string;
  default_uuid: string | undefined;
  defaultImageDimensions: ImageDimensions;
  selectedImageDimensions: ImageDimensions;
}

type payload = Array<ILabel | SavedLabel> | Array<IImage> | IWordsImage;

export interface IAction {
  type: string;
  payload: payload;
}

type Dispatch = {
  state: typeof initialState;
  dispatch: (action: IAction) => void;
  setLabels: (labels?: Array<ILabel | SavedLabel>) => void;
  setImages: (images?: Array<IImage>) => void;
  saveWordsImageUpdates: (prevState: IWordsImage, state: IWordsImage) => void;
  saveLabelsUpdates: (prevState: ILabel[] | SavedLabel[], state: ILabel[] | SavedLabel[]) => void;
  saveHideVisibleLabelUpdates: (prevState: ILabel[] | SavedLabel[], state: ILabel[] | SavedLabel[]) => void;
  saveShowHiddenLabelUpdates: (prevState: ILabel[] | SavedLabel[], state: ILabel[] | SavedLabel[]) => void;
  saveHideLabelsUpdates: (prevState: ILabel[] | SavedLabel[], state: ILabel[] | SavedLabel[]) => void;
};

const getProportionallyResizedImagePosition = (
  imageToReplacePosition: ImagePosition,
  defaultImageDimensions: ImageDimensions,
  selectedImageDimensions: ImageDimensions
) => {
  const defaultWidth = (imageToReplacePosition.width * defaultImageDimensions.exemplarWidth) / 100;
  const defaultHeight = (imageToReplacePosition.height * defaultImageDimensions.exemplarHeight) / 100;
  if (selectedImageDimensions.height > selectedImageDimensions.width) {
    const renderedWidth = (defaultHeight * selectedImageDimensions.width) / selectedImageDimensions.height;
    const left =
      imageToReplacePosition.x + ((defaultWidth - renderedWidth) / 2 / defaultImageDimensions.exemplarWidth) * 100;
    return {
      ...imageToReplacePosition,
      x: left,
      width: (renderedWidth / defaultImageDimensions.exemplarWidth) * 100,
    };
  } else {
    let renderedHeight = (defaultWidth * selectedImageDimensions.height) / selectedImageDimensions.width;
    const top =
      imageToReplacePosition.y + ((defaultHeight - renderedHeight) / 2 / defaultImageDimensions.exemplarHeight) * 100;

    if (renderedHeight > defaultHeight) {
      renderedHeight = defaultHeight;
      const renderedWidth = (defaultHeight * selectedImageDimensions.width) / selectedImageDimensions.height;
      const top =
        imageToReplacePosition.y + ((defaultHeight - renderedHeight) / 2 / defaultImageDimensions.exemplarHeight) * 100;
      const left =
        imageToReplacePosition.x + ((defaultWidth - renderedWidth) / 2 / defaultImageDimensions.exemplarWidth) * 100;
      return {
        ...imageToReplacePosition,
        x: left,
        y: top,
        height: (renderedHeight / defaultImageDimensions.exemplarHeight) * 100,
        width: (renderedWidth / defaultImageDimensions.exemplarWidth) * 100,
      };
    }
    return {
      ...imageToReplacePosition,
      y: top,
      height: (renderedHeight / defaultImageDimensions.exemplarHeight) * 100,
    };
  }
};

const replaceImage = (state: IState, action: IAction) => {
  const payload = action.payload as IWordsImage;
  const { imageUUIDtoReplace, uuid, default_uuid, defaultImageDimensions, selectedImageDimensions } = payload;
  return {
    ...state,
    images: state.images.map(image => {
      if (image.uuid === imageUUIDtoReplace && image.default_uuid === default_uuid) {
        if (!image.default_uuid) {
          return {
            ...image,
            position: getProportionallyResizedImagePosition(
              image.position,
              defaultImageDimensions,
              selectedImageDimensions
            ),
            defaultPosition: { ...image.position },
            uuid,
            default_uuid: image.uuid,
          };
        } else if (image.default_uuid && uuid === default_uuid) {
          return {
            ...image,
            uuid,
            position: {
              ...image.defaultPosition,
            },
          };
        } else {
          return {
            ...image,
            uuid,
            position: getProportionallyResizedImagePosition(
              image.defaultPosition,
              defaultImageDimensions,
              selectedImageDimensions
            ),
          };
        }
      } else return image;
    }),
  };
};

const wordsPageReducer = (state: IState, action: IAction): IState => {
  switch (action.type) {
    case ActionTypes.SET_IMAGES:
      return {
        ...state,
        images: action.payload as Array<IImage>,
      };
    case ActionTypes.SET_LABELS:
      return {
        ...state,
        labels: action.payload as Array<ILabel>,
      };
    case ActionTypes.REPLACE_IMAGE:
      return replaceImage(state, action);
    default:
      return state;
  }
};

export const WordsPageContext = createContext<Dispatch>({
  state: initialState,
  dispatch: () => false,
  setLabels: () => false,
  setImages: () => false,
  saveWordsImageUpdates: () => false,
  saveLabelsUpdates: () => false,
  saveHideVisibleLabelUpdates: () => false,
  saveShowHiddenLabelUpdates: () => false,
  saveHideLabelsUpdates: () => false,
});

export const WordsPageProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(wordsPageReducer, initialState);
  const { registerUndoRedoCreator } = useContext(UndoRedoContext);

  const setLabels = (labels: Array<ILabel | SavedLabel> = []) => {
    dispatch({ type: ActionTypes.SET_LABELS, payload: labels });
  };

  const setImages = (images: Array<IImage> = []) => {
    dispatch({ type: ActionTypes.SET_IMAGES, payload: images });
  };

  const saveImageUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.replaceImage, (payload: UndoRedoAction) => {
        dispatch({ type: ActionTypes.REPLACE_IMAGE, payload: payload as IWordsImage });
      }),
    []
  );

  const registerSetLabelsUndoRedoCreator = useCallback(
    (type: Type) =>
      registerUndoRedoCreator(type, (payload: UndoRedoAction) => {
        dispatch({ type: ActionTypes.SET_LABELS, payload: payload as ILabel[] | SavedLabel[] });
      }),
    [dispatch]
  );

  const saveLabelsUpdates = registerSetLabelsUndoRedoCreator(Type.setLabels);
  const saveHideVisibleLabelUpdates = registerSetLabelsUndoRedoCreator(Type.hideVisibleLabel);
  const saveShowHiddenLabelUpdates = registerSetLabelsUndoRedoCreator(Type.showHiddenLabel);
  const saveHideLabelsUpdates = registerSetLabelsUndoRedoCreator(Type.hideLabels);

  return (
    <WordsPageContext.Provider
      value={{
        dispatch,
        state,
        setLabels,
        setImages,
        saveWordsImageUpdates: saveImageUpdates,
        saveLabelsUpdates,
        saveHideVisibleLabelUpdates,
        saveShowHiddenLabelUpdates,
        saveHideLabelsUpdates,
      }}
    >
      {children}
    </WordsPageContext.Provider>
  );
};
